import { nanoid } from "nanoid";

const cache: { [key: string]: any } = {};

export function IsReactNative() {
  return typeof window !== "undefined" && "ReactNativeWebView" in window;
}

export type IReactNativeMessageCallback = (data: {
  type: string;
  data?: any;
  channel?: string;
}) => void;

export default function HandleReactNativeMessage(
  type: string,
  callback: IReactNativeMessageCallback
) {
  if (!IsReactNative()) return undefined;

  function messageHandler(event: MessageEvent) {
    const eventData = JSON.parse(event.data);
    if (eventData.type !== type) return;

    callback({
      type: eventData.type,
      data: eventData.data,
      channel: eventData.channel,
    });
  }

  document.addEventListener("message", messageHandler);

  return () => {
    document.removeEventListener("message", messageHandler);
  };
}

export function PostMessage(data: { type: string; data?: any; channel?: string }) {
  if (!IsReactNative()) return;

  (window as any).ReactNativeWebView.postMessage(
    JSON.stringify({
      type: data.type,
      data: data.data,
      channel: data.channel,
    })
  );
}

export interface IPostAsyncMessage {
  type: string;
  data?: any;
  cache?: boolean;
  timeoutMs?: number;
}

export function PostAsyncMessage<T>(data: IPostAsyncMessage): Promise<T> {
  if (!IsReactNative()) return undefined;

  return new Promise((resolve, reject) => {
    if (data.cache && cache[data.type]) {
      resolve(cache[data.type]);
      return;
    }

    const channel = nanoid();

    let timeout: NodeJS.Timeout;

    function messageHandler(event: MessageEvent) {
      const eventData = JSON.parse(event.data);

      if (eventData?.channel === channel) {
        clearTimeout(timeout);
        document.removeEventListener("message", messageHandler);
        if (data.cache) cache[data.type] = eventData.data;
        resolve(eventData.data);
      }
    }

    timeout = setTimeout(() => {
      document.removeEventListener("message", messageHandler);
      reject(new Error(`${data.type} timeout`));
    }, data.timeoutMs || 3000);

    document.addEventListener("message", messageHandler);

    PostMessage({
      type: data.type,
      data: data.data,
      channel,
    });
  });
}

export async function GetDeviceId(): Promise<string> {
  if (!IsReactNative()) return "dev";

  return PostAsyncMessage<string>({
    type: "GetDeviceId",
    cache: true,
  });
}

export function LoadingFinished() {
  PostMessage({
    type: "LoadingFinished",
  });
}

export function ToggleTabletEnvironment(toggle: boolean) {
  PostMessage({
    type: "ToggleTabletEnvironment",
    data: toggle,
  });
}

export function RequestCameraPermission() {
  return PostAsyncMessage<string>({
    type: "RequestCameraPermission",
    timeoutMs: 60000,
  });
}

export function SetScreenBrightness(brightness: number) {
  PostMessage({
    type: "SetScreenBrightness",
    data: brightness,
  });
}

export function GetScreenBrightness() {
  return PostAsyncMessage<number>({
    type: "GetScreenBrightness",
  });
}

export function SetKeepScreenOn(keepScreenOn: boolean) {
  return PostAsyncMessage<number>({
    type: "SetKeepScreenOn",
    data: keepScreenOn,
  });
}

export function IsDefaultHomeApp() {
  return PostAsyncMessage<boolean>({
    type: "IsDefaultHomeApp",
  });
}

export function ShowDefaultHomeAppSettings() {
  return PostAsyncMessage<boolean>({
    type: "ShowDefaultHomeAppSettings",
  });
}

export function Vibrate(pattern?: number | number[]) {
  PostMessage({
    type: "Vibrate",
    data: pattern,
  });
}

export function FlushCookies() {
  return PostAsyncMessage<void>({
    type: "FlushCookies",
  });
}
